import { memo, useState, useMemo, useCallback, type MouseEvent, type KeyboardEvent, type CSSProperties } from 'react'
import { twMerge } from 'tailwind-merge'
import { ChevronRight, Clock } from 'lucide-react'
import { Button, Cell, useKeyboardInteraction } from '@highlight/ui'
import { HOVER_CLUSTER_TRIGGER } from '../cell-hover-buttons'
import { CalendarEventIconView } from '@renderer/components/Icons/CalendarEventIcon'
import { useLinkedAudioNotes } from '@renderer/hooks/audio-notes'
import { useIsTakingNotes, useAudioNoteActiveNoteId } from '@renderer/hooks/audio/use-audio-note-selectors'
import {
  useDetectedMeetingForEvent,
  useIsRecordingCalendarEvent,
} from '@renderer/hooks/audio/use-recording-calendar-event'
import { useOpenBrief } from './brief/use-open-brief'
import { useMeetingBrief } from './brief/use-meeting-brief'
import { useLiveMeetingIdentity } from './use-live-meeting-identity'
import { MEETING_VIEW_ID } from './meeting-view-id'
import { BriefPreview } from './brief/components/brief-preview'
import { LiveRecordingPreview } from './live-recording-preview'
import { MeetingTimeLine } from './meeting-time-line'
import { openCalendarEventLink } from './open-calendar-event'
import { useJoinMeeting } from './use-join-meeting'
import { calendarEventToScheduledMeeting } from '@shared/utils/connections/calendars'
import { CalendarEvent } from '@highlight/common'
import { Participants } from '@renderer/overlay/components/participants'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { isWithinNextMinutes } from '@shared/utils/DateUtils'
import {
  isJoinWindowOpen,
  isSummaryReadyNote,
  meetingCellActions,
  useOpenMeetingSummary,
} from './meeting-quick-actions'
import { CELL_CONTEXT_MENU_TRIGGER, CellActionsCluster, CellActionsMenu, CellContextMenu } from '../cell-actions'
import { useCalendars } from '@renderer/hooks/use-calendars'
import { useViewActions, type ViewOpenTarget } from '../../view-atoms'
import { ACTIVE_MEETING_GLOW_STYLE, ACTIVE_MEETING_WASH_STYLE } from './constants'
import { useStartAudioNote } from '@renderer/hooks/useStartAudioNote'
import { isNoteForCalendarEvent } from '@shared/utils/audio-notes/AudioNoteUtils'

export interface MeetingCellProps {
  meeting: CalendarEvent
  sourceLabel?: string
  className?: string
  style?: CSSProperties
  joinButtonLabel?: string
  trackingTrigger?: string
  /** When true, the meeting title becomes a link to the calendar event URL. Off by default. */
  enableTitleLink?: boolean
  /** When true, render the inline BriefPreview card if a brief is ready. Off by default. */
  showBriefPreview?: boolean
}

export const MeetingCell = memo(function MeetingCell({
  meeting,
  sourceLabel = 'Home',
  className,
  style,
  joinButtonLabel,
  trackingTrigger = 'sidepanel',
  enableTitleLink = false,
  showBriefPreview: showBriefPreviewProp = false,
}: MeetingCellProps) {
  const isKeyboard = useKeyboardInteraction()
  const { joinLink, joinLabel, isAwaitingJoin, handleJoinClick } = useJoinMeeting(meeting, trackingTrigger)
  const resolvedJoinButtonLabel = joinButtonLabel ?? joinLabel
  const linkedAudioNotes = useLinkedAudioNotes(meeting)
  const { openView } = useViewActions()
  const liveMeeting = useLiveMeetingIdentity()
  const participants = useMemo(() => meeting.attendees.map((attendee) => attendee.email), [meeting.attendees])

  const { calendars } = useCalendars()

  const isTakingNotes = useIsTakingNotes()
  const activeNoteId = useAudioNoteActiveNoteId()
  const { startAudioNote } = useStartAudioNote()

  const matchedRecallMeeting = useDetectedMeetingForEvent(meeting)
  const isTranscribingThisMeeting = useIsRecordingCalendarEvent(meeting)
  const showStartRecording = !!matchedRecallMeeting && !isTakingNotes

  // Prefer calendarEventId match; fall back to time-range + title (the helper
  // gates by time overlap so a recurring meeting's future occurrence isn't
  // paired with a prior occurrence's note).
  const exactMatchAudioNote = linkedAudioNotes.find((n) => isNoteForCalendarEvent(n, meeting))
  const exactMatchAudioNoteId = exactMatchAudioNote?.id
  const exactMatchSavedToday = useMemo(
    () => !!exactMatchAudioNote && isSummaryReadyNote(exactMatchAudioNote),
    [exactMatchAudioNote],
  )
  const briefAudioNoteId = isTranscribingThisMeeting ? (activeNoteId ?? exactMatchAudioNoteId) : exactMatchAudioNoteId
  // Briefs are keyed on the calendar event, not the audio note — works for
  // upcoming meetings before any recording exists. Each occurrence of a
  // recurring meeting has its own event id and gets its own brief.
  const briefCalendarEventId = meeting.id
  // GlobalState only carries brief presence (conversationId); body is fetched.
  const hasBriefMeta = !!meeting.brief?.conversationId
  const { data: fetchedBrief } = useMeetingBrief(meeting.id, {
    enabled: showBriefPreviewProp && hasBriefMeta,
    pollWhilePending: hasBriefMeta,
    presenceConversationId: meeting.brief?.conversationId,
  })
  const legacyInlineBrief = meeting.brief?.brief?.trim() ? meeting.brief : null
  const inlineBrief = fetchedBrief ?? legacyInlineBrief
  const briefReady = !!inlineBrief?.brief?.trim()
  const showBriefPreview = showBriefPreviewProp && briefReady

  const briefGenerating = !!meeting.briefGenerating
  const hasBrief = hasBriefMeta || briefReady || briefGenerating

  const openBrief = useOpenBrief()

  const handleTitleClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation()
      if (!meeting.calendarEventLink) return
      // Plain left-click without modifier keys: take over and rewrite the URL.
      // Modifier-clicks (cmd/ctrl/middle) and right-clicks fall through to the
      // anchor's default behavior so the browser can open in a new tab / show
      // its context menu.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
      e.preventDefault()
      openCalendarEventLink(meeting)
    },
    [meeting],
  )

  const isHappeningNow = isWithinNextMinutes(meeting.start, 1, meeting.end)
  const isHappeningSoonOrNow = isJoinWindowOpen(meeting)
  // Title is clickable only when the parent explicitly opts in (via
  // enableTitleLink), the meeting has a calendar link, and it hasn't
  // ended yet. Off by default — only the inline upcoming-cell turns it on.
  const isUpcoming = !meeting.end || new Date(meeting.end).getTime() > Date.now()
  const isTitleClickable = enableTitleLink && isUpcoming && !!meeting.calendarEventLink
  // Dimmed/past treatment for meetings that have already ended — unless the
  // recording is still running past its scheduled end (still the live focus).
  const isPastMeeting = !isUpcoming && !isTranscribingThisMeeting
  // Brief auto-generation now lives in the main process — see
  // ScheduledMeetingService's 'scheduled-meeting:trigger-brief' event,
  // subscribed in use-meeting-toasts.tsx. Decoupled from cell render so
  // generation kicks off regardless of sidepanel state.

  const openBriefTarget = useCallback(
    (open: ViewOpenTarget) => {
      // While this meeting is being transcribed, the live transcript is the
      // priority — open the Meeting View on the running session (which defaults
      // to Transcript) rather than its brief.
      if (isTranscribingThisMeeting) {
        openView(MEETING_VIEW_ID, liveMeeting, { sourceLabel, ...open })
        return
      }
      if (!briefCalendarEventId) return
      openBrief({
        calendarEventId: briefCalendarEventId,
        audioNoteId: briefAudioNoteId,
        isActive: isTranscribingThisMeeting,
        title: meeting.title,
        meeting,
        ...open,
      })
      AmplitudeRenderer.trackEvent('Meeting Brief', {
        action: 'view',
        source: 'meeting-cell',
        meeting_id: briefCalendarEventId,
      })
    },
    [
      briefCalendarEventId,
      briefAudioNoteId,
      isTranscribingThisMeeting,
      liveMeeting,
      meeting,
      openBrief,
      openView,
      sourceLabel,
    ],
  )

  const handleViewClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation()
      openBriefTarget({ pointerEvent: e })
    },
    [openBriefTarget],
  )

  const handleStartRecordingClick = useCallback(
    async (e: MouseEvent) => {
      e.stopPropagation()
      if (!matchedRecallMeeting) return
      AmplitudeRenderer.trackEvent('Audio Note Action', {
        action: 'start transcript from meeting list',
        trigger: trackingTrigger,
      })
      startAudioNote({ source: 'base', meeting: matchedRecallMeeting })
    },
    [matchedRecallMeeting, startAudioNote, trackingTrigger],
  )

  const handleViewTranscriptClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation()
      openView(MEETING_VIEW_ID, liveMeeting, { sourceLabel, pointerEvent: e })
    },
    [liveMeeting, openView, sourceLabel],
  )

  // A meeting with no join link can't be joined, so start a manual recording
  // tied to the calendar event. Recording a scheduled-meeting shape (id = event
  // id) makes isTranscribingThisMeeting match, flipping this CTA to
  // "View Transcript" once recording begins.
  const handleTranscribeWithoutLinkClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation()
      AmplitudeRenderer.trackEvent('Audio Note Action', {
        action: 'start transcript from meeting list',
        trigger: trackingTrigger,
      })
      startAudioNote({ source: 'base', meeting: calendarEventToScheduledMeeting(meeting) })
    },
    [startAudioNote, meeting, trackingTrigger],
  )

  const openSummary = useOpenMeetingSummary(meeting)
  const openSummaryTarget = useCallback(
    (open: ViewOpenTarget, trigger: string) => {
      if (!exactMatchAudioNoteId) return
      openSummary(exactMatchAudioNoteId, trigger, open)
    },
    [exactMatchAudioNoteId, openSummary],
  )
  const handleViewSummaryClick = useCallback(
    (e: MouseEvent, triggerOverride?: string) => {
      e.stopPropagation()
      openSummaryTarget({ pointerEvent: e }, triggerOverride ?? trackingTrigger)
    },
    [openSummaryTarget, trackingTrigger],
  )

  const [isFocused, setIsFocused] = useState(false)

  const canViewTranscript = isTranscribingThisMeeting
  const canStartRecording = showStartRecording
  const canJoin = !!(joinLink && isHappeningSoonOrNow) && !showStartRecording && !isTranscribingThisMeeting
  // No join link → offer to transcribe the meeting directly (mirrors where Join
  // would otherwise sit). Hidden while another note is recording, since a manual
  // start won't interrupt an in-progress session.
  const canTranscribeWithoutLink =
    !joinLink && isHappeningSoonOrNow && !showStartRecording && !isTranscribingThisMeeting && !isTakingNotes
  const canView = hasBrief
  const canViewSummary = !!exactMatchAudioNoteId && exactMatchSavedToday && !isTranscribingThisMeeting

  // What clicking the cell (or pressing Enter) does: the Brief if one exists,
  // then a saved summary from earlier today, then the meeting itself. It always
  // drills in — the Meeting View renders a meeting with nothing recorded yet
  // perfectly well, and being sent out to the calendar instead was the
  // surprising part of clicking a meeting. The calendar event is still one click
  // away, from the title link here and from the view's own header.
  //
  // The plain case deliberately doesn't route through `openBrief`: that path
  // reads an open as consent to generate a brief, and this is now just
  // navigation.
  const openMeetingTarget = useCallback(
    (open: ViewOpenTarget) => {
      openView(
        MEETING_VIEW_ID,
        { mode: 'note', calendarEventId: meeting.id, title: meeting.title, meeting },
        { sourceLabel, ...open },
      )
    },
    [meeting, openView, sourceLabel],
  )

  const openPrimary = useMemo(() => {
    if (canView) return handleViewClick
    if (canViewSummary) return handleViewSummaryClick
    return (e: MouseEvent) => {
      e.stopPropagation()
      openMeetingTarget({ pointerEvent: e })
    }
  }, [canView, canViewSummary, handleViewClick, handleViewSummaryClick, openMeetingTarget])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !e.metaKey) {
        e.preventDefault()
        e.stopPropagation()
        openPrimary?.({ stopPropagation: () => {} } as unknown as MouseEvent)
      } else if (e.metaKey && e.key === 'Enter') {
        e.preventDefault()
        e.stopPropagation()
        if (canViewTranscript) {
          handleViewTranscriptClick({
            stopPropagation: () => {},
          } as unknown as MouseEvent)
        } else if (canStartRecording) {
          handleStartRecordingClick({
            stopPropagation: () => {},
          } as unknown as MouseEvent)
        } else if (canJoin) {
          handleJoinClick({
            stopPropagation: () => {},
          } as unknown as MouseEvent)
        } else if (canTranscribeWithoutLink) {
          handleTranscribeWithoutLinkClick({
            stopPropagation: () => {},
          } as unknown as MouseEvent)
        } else if (canView) {
          handleViewClick({
            stopPropagation: () => {},
          } as unknown as MouseEvent)
        } else if (canViewSummary) {
          handleViewSummaryClick({
            stopPropagation: () => {},
          } as unknown as MouseEvent)
        }
      }
    },
    [
      openPrimary,
      canViewTranscript,
      canStartRecording,
      canJoin,
      canTranscribeWithoutLink,
      canView,
      canViewSummary,
      handleViewTranscriptClick,
      handleStartRecordingClick,
      handleJoinClick,
      handleTranscribeWithoutLinkClick,
      handleViewClick,
      handleViewSummaryClick,
    ],
  )

  const title = isTitleClickable ? (
    <a
      href={meeting.calendarEventLink}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleTitleClick}
      className="text-foreground-secondary hover:decoration-foreground-secondary underline decoration-transparent transition-[text-decoration-color] duration-150"
    >
      {meeting.title}
    </a>
  ) : (
    meeting.title
  )

  const showTranscribeButton = !isTranscribingThisMeeting && showStartRecording
  // Join stays in the footer, unlike View Summary. This cell is Home's lead
  // card, so an open join window means this is the meeting to be in, and the
  // design gives it the full CTA at rest (Figma 6972:11459, `Active=Soon` and
  // `Active=True`). The flat rows have no footer, so there Join is a hover glyph.
  const hasFooterActions = showTranscribeButton || canTranscribeWithoutLink || canJoin

  // The footer's left slot, opposite the CTA (Figma 6972:11459 `Active=Soon`).
  // Once recording starts, LiveRecordingPreview is the status and carries its
  // own View button, so a second status line would say the same thing twice.
  const meetingStatus = isHappeningSoonOrNow && !isTranscribingThisMeeting
  const meetingStatusLabel = isHappeningNow ? 'Happening now' : 'Happening soon'

  const footer = (hasFooterActions || meetingStatus) && (
    <Cell.Footer className={meetingStatus ? undefined : 'justify-end'}>
      {meetingStatus && (
        <div className="text-foreground type-ui-xs flex min-w-0 items-center gap-1 font-medium leading-none">
          <Clock aria-hidden="true" className="size-3 shrink-0" />
          <span className="truncate">{meetingStatusLabel}</span>
        </div>
      )}
      <div className="flex items-center gap-2">
        {canJoin && (
          <Button
            onClick={handleJoinClick}
            // The inverse fill means "you're in this one". While a transcript is recording
            // elsewhere it belongs to that meeting's View Transcript button, so
            // Join drops to outline and back-to-back calls stay tellable apart.
            variant={isTakingNotes ? 'outline' : 'primary'}
            tone={isTakingNotes ? 'neutral' : 'inverse'}
            loading={isAwaitingJoin}
            hotkey={isFocused && canJoin ? 'cmd + enter' : undefined}
          >
            {resolvedJoinButtonLabel}
          </Button>
        )}
        {showTranscribeButton && (
          <Button
            onClick={handleStartRecordingClick}
            variant="primary"
            tone={isHappeningNow ? 'inverse' : 'neutral'}
            hotkey={isFocused && canStartRecording ? 'cmd + enter' : undefined}
          >
            Transcribe
          </Button>
        )}
        {canTranscribeWithoutLink && (
          <Button
            onClick={handleTranscribeWithoutLinkClick}
            variant="primary"
            tone={isHappeningNow ? 'inverse' : 'neutral'}
            hotkey={isFocused && canTranscribeWithoutLink ? 'cmd + enter' : undefined}
          >
            Transcribe
          </Button>
        )}
      </div>
    </Cell.Footer>
  )

  // Same actions the footer used to carry, but a distinct entry point — the
  // trigger says so, prefixed with the host surface's own trigger.
  const clusterTrigger = `${trackingTrigger}-${HOVER_CLUSTER_TRIGGER}`

  // Wherever a click would land, in a window: the brief, then a summary saved
  // earlier today, then the meeting itself. Withheld while this meeting is
  // recording, where the cluster would sit on top of the live preview's View
  // button — the one control that matters mid-meeting.
  const popoutPrimary = isTranscribingThisMeeting
    ? undefined
    : () => {
        if (canView) openBriefTarget({ popout: true })
        else if (canViewSummary) openSummaryTarget({ popout: true }, clusterTrigger)
        else openMeetingTarget({ popout: true })
      }

  // No Join glyph. The card renders Join in its footer, and a copy here would
  // put two live Join buttons on one meeting under the pointer.
  const clusterActions = meetingCellActions({
    showSummary: canViewSummary,
    onViewSummary: (e) => handleViewSummaryClick(e, clusterTrigger),
  })
  const menuActions = meetingCellActions({
    showSummary: canViewSummary,
    onViewSummary: (e) => handleViewSummaryClick(e, `${trackingTrigger}-${CELL_CONTEXT_MENU_TRIGGER}`),
  })

  // The wash covers the whole join window and any overrunning recording, so it
  // arrives with the Join CTA. The ring on top of it stays the narrower "this
  // one is live right now" signal.
  const showAccentWash = isHappeningSoonOrNow || isTranscribingThisMeeting
  const cellStyle =
    showAccentWash || isHappeningNow
      ? {
          ...style,
          ...(showAccentWash ? ACTIVE_MEETING_WASH_STYLE : null),
          ...(isHappeningNow ? ACTIVE_MEETING_GLOW_STYLE : null),
        }
      : style
  // The hairline drops from under the icon and runs alongside the time line into
  // the brief box, so it only makes sense when there's a brief to reach.
  const showConnector = showBriefPreview

  return (
    <CellContextMenu items={<CellActionsMenu actions={menuActions} onPopout={popoutPrimary} />}>
      <div className="group/action relative">
        <Cell
          className={twMerge('rounded-xl', isPastMeeting && 'opacity-45', className)}
          style={cellStyle}
          focused={isFocused}
          onFocus={() => {
            if (isKeyboard()) setIsFocused(true)
          }}
          onBlur={() => setIsFocused(false)}
          onKeyDown={handleKeyDown}
          onClick={openPrimary}
        >
          <Cell.Header showChevron={false}>
            <Cell.Icon>
              <CalendarEventIconView event={meeting} size={14} calendars={calendars} />
            </Cell.Icon>
            <Cell.Title className="text-foreground line-clamp-1 font-normal">{title}</Cell.Title>
            {participants.length > 0 && <Participants participants={participants} />}
            <ChevronRight className="text-foreground-secondary ml-auto size-4 shrink-0" aria-hidden="true" />
          </Cell.Header>
          {/* Second line under the title. `pl-7` clears the 20px icon plus the
              header's 8px gap so it starts at the title's text edge; `-mt-1.5`
              tightens the Cell's 12px gap to the design's 6px. */}
          <div className="relative -mt-1.5 basis-full pl-7">
            {showConnector && (
              <span aria-hidden="true" className="bg-element-hover absolute -bottom-3 -top-1.5 left-2 w-px" />
            )}
            <MeetingTimeLine meeting={meeting} />
          </div>
          {showBriefPreview && inlineBrief && (
            <BriefPreview
              brief={inlineBrief.brief}
              relevantLinks={inlineBrief.relevantLinks}
              showLinks={false}
              onClick={handleViewClick}
              // Same surface colour as the card it sits in (per Figma) — the hairline
              // edge and soft shadow are what separate it, not a fill.
              className="corner-smooth rounded-lg border-0 bg-transparent px-3 py-2 text-xs shadow-md"
            />
          )}
          {isTranscribingThisMeeting && (
            <LiveRecordingPreview
              onView={handleViewTranscriptClick}
              viewHotkey={isFocused && canViewTranscript ? 'cmd + enter' : undefined}
            />
          )}
          {footer}
        </Cell>
        <CellActionsCluster actions={clusterActions} onPopout={popoutPrimary} />
      </div>
    </CellContextMenu>
  )
})
